import { useEffect, useRef, useState } from "react";
import { clearTempStoreDB, deleteItem, getItem } from "../../../utilities/localStore/indexDBCalls";
import ConfirmDialog from "../ConfirmDialog";
import { getFileIcon } from "../file";
import "./FilePickerDialog.scss";

export interface iTempStoreFile {
    id: number;
    mimeType: string;
    name: string;
    size: string;
    date: Date;
}

export interface FilePickerDialogProps {
    onConfirm: (selectedFiles: iTempStoreFile[]) => void;
    onCancel: Function;
    title?: string | undefined;
}

function formatSize(size: string): string {
    const [value, unit] = size.split(" ");
    const numericValue = parseFloat(value);
    return isNaN(numericValue) ? size : `${numericValue.toFixed(2)} ${unit ?? ""}`.trim();
}

function FilePickerDialog(props: FilePickerDialogProps) {
    const [files, setFiles] = useState<iTempStoreFile[]>([]);
    const [filteredFiles, setFilteredFiles] = useState<iTempStoreFile[]>([]);
    const [selectedFileId, setSelectedFileId] = useState<number | undefined>(undefined);
    const [checkedFileIds, setCheckedFileIds] = useState<Set<number>>(new Set());

    const selectedFileRef = useRef<iTempStoreFile | undefined>(undefined);

    function toggleFileChecked(id: number) {
        setCheckedFileIds(current => {
            const next = new Set(current);
            if (next.has(id)) {
                next.delete(id);
            } else {
                next.add(id);
            }
            return next;
        });
    }

    useEffect(() => {
        (async () => {
            await readTempStore();
        })();
    }, []);

    useEffect(() => {
        selectedFileRef.current = files.find(x => x.id === selectedFileId);
    }, [selectedFileId, files]);

    function OnFilter(filterValue: string) {
        setFilteredFiles(files.filter(x => x.name.toLowerCase().indexOf(filterValue.toLowerCase()) >= 0));
    }

    async function confirmSelection(selectedFiles: iTempStoreFile[]) {
        if (selectedFiles.length > 0) {
            props.onConfirm(selectedFiles);
        }
    }
    const readTempStore = async () => {
        const result: iTempStoreFile[] = (await getItem("TempStore", "all")) ?? [];
        result.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
        setFiles(result);
        setFilteredFiles(result);
        setCheckedFileIds(current => new Set([...current].filter(id => result.some(f => f.id === id))));
    };
    const firstTabElement = useRef<HTMLInputElement>(null);
    const parentList = useRef<HTMLInputElement>(null);
    const keydownHandler = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key == " ") {
            event.preventDefault();
            if (selectedFileId !== undefined) {
                toggleFileChecked(selectedFileId);
            }
            return;
        }
        if (event.key != "ArrowUp" && event.key != "ArrowDown") return;
        const currentSelectedIndex = filteredFiles.findIndex(x => x.id == selectedFileId);
        if (event.key == "ArrowUp") {
            if (currentSelectedIndex > 0) {
                setSelectedFileId(filteredFiles[currentSelectedIndex - 1].id);
                (parentList.current?.children[currentSelectedIndex - 1] as HTMLElement).focus();
            } else {
                setSelectedFileId(filteredFiles[currentSelectedIndex].id);
                (parentList.current?.children[currentSelectedIndex] as HTMLElement).focus();
            }
        } else {
            if (currentSelectedIndex < filteredFiles.length - 1) {
                setSelectedFileId(filteredFiles[currentSelectedIndex + 1].id);
                (parentList.current?.children[currentSelectedIndex + 1] as HTMLElement).focus();
            } else {
                setSelectedFileId(filteredFiles[currentSelectedIndex].id);
                (parentList.current?.children[currentSelectedIndex] as HTMLElement).focus();
            }
        }
    };
    const deleteItemsFromStorage = async () => {
        const idsToDelete =
            checkedFileIds.size > 0
                ? Array.from(checkedFileIds)
                : selectedFileRef.current
                  ? [selectedFileRef.current.id]
                  : [];
        for (const id of idsToDelete) {
            await deleteItem("Blobs", id);
            await deleteItem("TempStore", id);
        }
        await readTempStore();
    };

    return (
        <>
            <ConfirmDialog
                title={props.title ?? "Select a File"}
                onConfirm={() => confirmSelection(files.filter(f => checkedFileIds.has(f.id)))}
                onCancel={props.onCancel}
                isValid={checkedFileIds.size > 0}
            >
                <div style={{ width: "550px" }}>
                    <span
                        tabIndex={0}
                        onFocus={() => firstTabElement.current?.focus()}
                    />
                    <div className="row align-items-center formRowMargin">
                        <div className="col-sm-2">
                            <label>Filter:</label>
                        </div>
                        <div className="col-sm-10">
                            <input
                                type="text"
                                className="form-control form-control-sm"
                                onChange={e => OnFilter(e.currentTarget.value)}
                                autoFocus={true}
                                autoComplete="off"
                                aria-autocomplete="none"
                                list="autocompleteOff"
                                ref={firstTabElement}
                            />
                        </div>
                    </div>
                    <div
                        className="file-picker-options"
                        ref={parentList}
                    >
                        {filteredFiles.length === 0 && <div className="file-picker-empty">No files found.</div>}
                        {filteredFiles.map(file => (
                            <div
                                tabIndex={0}
                                key={"file-picker-option-" + file.id}
                                className={`file-picker-option ${
                                    file.id === selectedFileId ? "file-picker-option-selected" : ""
                                }`}
                                onFocus={() => setSelectedFileId(file.id)}
                                onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>): void => keydownHandler!(e)}
                                onClick={() => toggleFileChecked(file.id)}
                                onDoubleClick={() => confirmSelection([file])}
                            >
                                <div style={{ marginRight: "-5px" }}>
                                    <input
                                        type="checkbox"
                                        checked={checkedFileIds.has(file.id)}
                                        onClick={e => e.stopPropagation()}
                                        onChange={() => toggleFileChecked(file.id)}
                                        className="g-Checkbox-default"
                                        style={{ marginTop: "3px" }}
                                    />
                                </div>
                                <div style={{ width: "22px" }}>{getFileIcon(file.mimeType, file.name)}</div>
                                <span className="file-picker-option-name">{file.name}</span>
                            </div>
                        ))}
                    </div>
                    <div className="delButton">
                        <button
                            className={checkedFileIds.size > 0 ? "  g-btn g-btn-alternate" : "  g-btn g-btn-disabled"}
                            style={{ marginRight: "15px" }}
                            onClick={() => {
                                const count =
                                    checkedFileIds.size > 0 ? checkedFileIds.size : selectedFileRef.current ? 1 : 0;
                                if (count > 0) {
                                    let result = window.confirm(
                                        count > 1
                                            ? `Are you sure you want to delete the ${count} selected items`
                                            : "Are you sure you want to delete the item",
                                    );
                                    if (result) {
                                        deleteItemsFromStorage();
                                    }
                                }
                            }}
                        >
                            Delete Item
                        </button>
                        <button
                            className={`g-btn ${files.length == 0 ? "g-btn-disabled" : "g-btn-action"}`}
                            onClick={async () => {
                                let result = window.confirm("Are you sure you want to clear Temporary Store");
                                if (result) {
                                    await clearTempStoreDB();
                                    await readTempStore();
                                }
                            }}
                        >
                            Clear Temporary Store
                        </button>
                    </div>
                </div>
            </ConfirmDialog>
            <span
                tabIndex={0}
                onFocus={() => firstTabElement.current?.focus()}
            />
        </>
    );
}
export default FilePickerDialog;
